// DOM → constrained-markdown serializer for the WYSIWYG answer editor.
// Inverse of markdownToEmailHtml over the answer subset: <p>/<div> blocks,
// <br> line breaks, <ul>/<ol> flat lists, <strong>/<b> and <em>/<i>. Anything
// outside the subset (spans, colors, pasted markup) contributes only its
// text. This reads DOM STRUCTURE, not innerText — the old contentEditable
// editing was lossy precisely because innerText drops list/emphasis markup.

const wrapSingleLine = (marker: string, content: string): string => {
  // CommonMark emphasis may not start/end with whitespace — hoist edge
  // whitespace outside the markers.
  const lead = /^\s*/.exec(content)?.[0] ?? "";
  const trail = /\s*$/.exec(content)?.[0] ?? "";
  const core = content.trim();
  return core ? `${lead}${marker}${core}${marker}${trail}` : content;
};

const wrapMarker = (marker: string, content: string): string =>
  // Emphasis spanning a <br> must close and reopen per line: the subset's
  // renderer (and hard-break markdown generally) does not carry emphasis
  // across line breaks, so "**a\nb**" would show its markers literally.
  content
    .split("\n")
    .map((line) => wrapSingleLine(marker, line))
    .join("\n");

// Matches the inline citation formats the backend emits (see
// stripCitationMarkers): "[ref:1]" and grouped "[ref:1, ref:3]" / "[ref:1, 3]".
const CITATION_MARKER_RE = /\[ref:\s*\d+(?:\s*,\s*(?:ref:\s*)?\d+)*\]/g;

/**
 * Replace literal citation markers in converter HTML with inert, atomic
 * chip elements for the WYSIWYG editor: visible as index badges, not
 * editable from inside (contenteditable=false — deletable only as a whole),
 * and carrying the original marker in data-ref-marker so serialization
 * restores it byte-for-byte. Runs on markdownToEmailHtml output, where all
 * text is already escaped; the marker itself contains no HTML-active
 * characters.
 */
export function decorateCitationTokens(html: string): string {
  return html.replace(CITATION_MARKER_RE, (marker) => {
    const indices = (marker.match(/\d+/g) ?? []).join(" ");
    return `<span class="citation-token" contenteditable="false" data-ref-marker="${marker}">${indices}</span>`;
  });
}

const serializeInline = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? "";
  if (!(node instanceof HTMLElement)) return "";
  const refMarker = node.getAttribute("data-ref-marker");
  if (refMarker) return refMarker;
  const children = Array.from(node.childNodes).map(serializeInline).join("");
  switch (node.tagName) {
    case "BR":
      return "\n";
    case "B":
    case "STRONG":
      return wrapMarker("**", children);
    case "I":
    case "EM":
      return wrapMarker("*", children);
    default: {
      // WebKit sometimes styles spans instead of emitting <b>/<i>
      // (e.g. style="font-weight: bold") — read the inline style so bold
      // applied in Safari survives the save.
      let out = children;
      const { fontStyle, fontWeight } = node.style;
      if (fontStyle === "italic") out = wrapMarker("*", out);
      if (fontWeight === "bold" || fontWeight === "bolder" || parseInt(fontWeight, 10) >= 600) {
        out = wrapMarker("**", out);
      }
      return out;
    }
  }
};

// Collapse horizontal-space runs (e.g. from whitespace hoisted out of
// emphasis markers) — mirrors the backend's whitespace normalization.
const tidy = (s: string) => s.replace(/[^\S\n]{2,}/g, " ");

const BLOCK_TAGS = new Set(["P", "DIV", "UL", "OL"]);

const hasBlockChild = (el: HTMLElement): boolean =>
  Array.from(el.children).some((child) => BLOCK_TAGS.has(child.tagName));

const serializeList = (list: HTMLElement, blocks: string[]): void => {
  // A <br> inside an <li> splits into separate items: the subset only knows
  // single-line items, and execCommand often converts a whole
  // "line<br>line<br>line" paragraph into ONE <li> — the operator meant one
  // bullet per line.
  const items = Array.from(list.children)
    .filter((child) => child.tagName === "LI")
    .flatMap((li) => serializeInline(li).split(/\n+/))
    .map((item) => tidy(item).trim())
    .filter(Boolean);
  if (items.length) {
    blocks.push(
      list.tagName === "UL"
        ? items.map((item) => `- ${item}`).join("\n")
        : items.map((item, i) => `${i + 1}. ${item}`).join("\n"),
    );
  }
};

const collectBlocks = (container: HTMLElement, blocks: string[]): void => {
  let pending: string[] = [];
  const flushPending = () => {
    const text = tidy(pending.join("")).replace(/\n{2,}/g, "\n").trim();
    if (text) blocks.push(text);
    pending = [];
  };

  for (const node of Array.from(container.childNodes)) {
    if (node instanceof HTMLElement && (node.tagName === "UL" || node.tagName === "OL")) {
      flushPending();
      serializeList(node, blocks);
    } else if (node instanceof HTMLElement && (node.tagName === "P" || node.tagName === "DIV")) {
      flushPending();
      if (hasBlockChild(node)) {
        // execCommand can leave a list (or further wrappers) NESTED inside
        // the paragraph it was applied in — recurse so the structure
        // serializes as blocks instead of degrading to inline text.
        collectBlocks(node, blocks);
      } else {
        const text = tidy(serializeInline(node).replace(/\n{2,}/g, "\n")).trim();
        if (text) blocks.push(text);
      }
    } else {
      pending.push(serializeInline(node));
    }
  }
  flushPending();
};

/**
 * Serialize the WYSIWYG editor's DOM back into constrained markdown.
 *
 * Block model: each <p>/<div> is a paragraph (contentEditable creates a
 * <div> per Enter), <br> inside a paragraph is a hard line break, <ul>/<ol>
 * become flat "- "/"1." lines — wherever they sit, since execCommand nests
 * freshly created lists inside the block it was applied in. Paragraphs join
 * with blank lines. The guaranteed invariant (unit-tested) is render
 * equivalence: markdownToEmailHtml(serialize(render(md))) === render(md).
 */
export function editorHtmlToMarkdown(root: HTMLElement): string {
  const blocks: string[] = [];
  collectBlocks(root, blocks);
  return blocks.join("\n\n");
}

/**
 * Clipboard flavors for a manual selection-copy inside the answer.
 *
 * The browser's native copy serializes the rendered DOM, whose line-break
 * semantics do not match what the copy button produces — pasted into
 * plain-text targets (e.g. Advasco) the lines shift. Routing the selection
 * fragment through the same DOM→markdown serializer makes select+copy
 * byte-consistent with the button: citation chips are dropped, emphasis
 * markers stripped from the text flavor, real markup in the HTML flavor.
 * Returns null for an empty/whitespace selection (caller keeps native copy).
 */
export function selectionToClipboardFlavors(
  selection: Selection,
): { markdown: string } | null {
  if (selection.isCollapsed || selection.rangeCount === 0) return null;
  const container = document.createElement("div");
  for (let i = 0; i < selection.rangeCount; i++) {
    container.appendChild(selection.getRangeAt(i).cloneContents());
  }
  const markdown = editorHtmlToMarkdown(container);
  return markdown.trim() ? { markdown } : null;
}
